<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useWebSiteStore } from '@/stores/web-site.store'
import { ABSENCE_COLOR, ABSENCE_ICON } from '@/stores/schedule.store'

const props = defineProps({
  staffOptions: {
    type: Array,
    required: true,
  },
  staffIds: {
    type: Array,
    required: true,
  },
  equipmentOptions: {
    type: Array,
    required: true,
  },
  equipmentIds: {
    type: Array,
    required: true,
  },
  projectOptions: {
    type: Array,
    required: true,
  },
  projectIds: {
    type: Array,
    required: true,
  },
  showAbsences: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:staffIds', 'update:equipmentIds', 'update:projectIds', 'update:showAbsences'])

const { t } = useI18n()
const webSiteStore = useWebSiteStore()

const search = ref('')
const collapsedSections = ref(new Set())

const matchesSearch = (option) => !search.value || option.label?.toLowerCase().includes(search.value.toLowerCase())

const sections = computed(() => [
  {
    key: 'staff',
    label: t('common.staff'),
    icon: 'mdi-account-multiple-outline',
    options: props.staffOptions,
    selected: new Set(props.staffIds),
    event: 'update:staffIds',
  },
  {
    key: 'equipment',
    label: t('common.equipment', 2),
    icon: 'mdi-camcorder',
    options: props.equipmentOptions,
    selected: new Set(props.equipmentIds),
    event: 'update:equipmentIds',
  },
  {
    key: 'projects',
    label: t('common.project', 2),
    icon: 'mdi-briefcase-outline',
    options: props.projectOptions,
    selected: new Set(props.projectIds),
    event: 'update:projectIds',
  },
].map(section => ({ ...section, visibleOptions: section.options.filter(matchesSearch) })))

const allState = (section) => {
  if (section.selected.size === 0) {
    return false
  }
  return section.selected.size >= section.options.length ? true : null
}

const toggleOption = (section, id) => {
  const selected = new Set(section.selected)
  if (selected.has(id)) {
    selected.delete(id)
  } else {
    selected.add(id)
  }
  emit(section.event, [...selected])
}

const toggleAll = (section) => {
  emit(section.event, allState(section) === true ? [] : section.options.map(option => option.id))
}

const showOnly = (section, id) => {
  emit(section.event, [id])
}

const toggleCollapsed = (key) => {
  const collapsed = new Set(collapsedSections.value)
  if (collapsed.has(key)) {
    collapsed.delete(key)
  } else {
    collapsed.add(key)
  }
  collapsedSections.value = collapsed
}
</script>
<template>
  <div class="cal-filters">
    <q-input
      v-model="search"
      dense
      outlined
      rounded
      clearable
      class="cal-filters__search"
      :placeholder="t('common.search')"
      :debounce="webSiteStore.getInputDebounce()"
    >
      <template #prepend>
        <q-icon name="mdi-magnify" size="xs" />
      </template>
    </q-input>

    <button
      type="button"
      class="cal-filters__row cal-filters__row--absences"
      @click="emit('update:showAbsences', !showAbsences)"
    >
      <span
        class="cal-filters__check"
        :class="{ 'cal-filters__check--on': showAbsences }"
        :style="{ '--check-color': ABSENCE_COLOR }"
      >
        <q-icon name="mdi-check" size="0.75rem" />
      </span>
      <q-icon :name="ABSENCE_ICON" size="xs" :style="{ color: ABSENCE_COLOR }" />
      <span class="cal-filters__label">{{ t('common.vacation', 2) }}</span>
    </button>

    <section
      v-for="section in sections"
      :key="section.key"
      class="cal-filters__section"
    >
      <div class="cal-filters__section-header">
        <button
          type="button"
          class="cal-filters__section-title"
          @click="toggleCollapsed(section.key)"
        >
          <q-icon :name="section.icon" size="xs" />
          <span class="cal-filters__label">{{ section.label }}</span>
          <span class="cal-filters__count">{{ section.selected.size }}/{{ section.options.length }}</span>
          <q-icon
            name="mdi-chevron-down"
            size="xs"
            class="cal-filters__chevron"
            :class="{ 'cal-filters__chevron--collapsed': collapsedSections.has(section.key) }"
          />
        </button>
        <q-checkbox
          :model-value="allState(section)"
          size="sm"
          dense
          toggle-indeterminate
          @update:model-value="toggleAll(section)"
        >
          <q-tooltip>{{ t('common.all') }}</q-tooltip>
        </q-checkbox>
      </div>
      <q-slide-transition>
        <div v-show="!collapsedSections.has(section.key)" class="cal-filters__list">
          <div
            v-for="option in section.visibleOptions"
            :key="option.id"
            class="cal-filters__row"
            role="checkbox"
            :aria-checked="section.selected.has(option.id)"
            @click="toggleOption(section, option.id)"
          >
            <span
              class="cal-filters__check"
              :class="{ 'cal-filters__check--on': section.selected.has(option.id) }"
              :style="{ '--check-color': option.color }"
            >
              <q-icon name="mdi-check" size="0.75rem" />
            </span>
            <span class="cal-filters__label">{{ option.label }}</span>
            <q-btn
              flat
              round
              dense
              size="xs"
              icon="mdi-eye-outline"
              class="cal-filters__only"
              @click.stop="showOnly(section, option.id)"
            >
              <q-tooltip>{{ t('common.showOnly') }}</q-tooltip>
            </q-btn>
          </div>
        </div>
      </q-slide-transition>
    </section>
  </div>
</template>
<style scoped lang="scss">
.cal-filters {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0;
}

.cal-filters__search :deep(.q-field__control) {
  height: 2.25rem;
  min-height: 2.25rem;
}

.cal-filters__search :deep(.q-field__marginal) {
  height: 2.25rem;
}

.cal-filters__section {
  display: flex;
  flex-direction: column;
}

.cal-filters__list {
  max-height: 15rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--q-primary) 30%, transparent) transparent;
}

.cal-filters__section-header {
  display: flex;
  align-items: center;
  padding-right: 0.25rem;
}

.cal-filters__section-title {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.5rem 0.5rem;
  border: 0;
  border-radius: 0.5rem;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: color-mix(in srgb, var(--q-primary) 6%, transparent);
  }
}

.cal-filters__count {
  padding: 0 0.375rem;
  border-radius: 1rem;
  background: var(--cal-surface-alt);
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--cal-text-muted);
}

.cal-filters__chevron {
  margin-left: auto;
  color: var(--cal-text-muted);
  transition: transform 0.2s ease;
}

.cal-filters__chevron--collapsed {
  transform: rotate(-90deg);
}

.cal-filters__row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  min-height: 2rem;
  padding: 0.25rem 0.5rem;
  border: 0;
  border-radius: 0.5rem;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.8125rem;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: color-mix(in srgb, var(--q-primary) 6%, transparent);
  }

  &:hover .cal-filters__only {
    opacity: 1;
  }
}

.cal-filters__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.cal-filters__check {
  --check-color: var(--q-primary);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  border: 2px solid var(--check-color);
  border-radius: 0.3125rem;
  color: transparent;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.cal-filters__check--on {
  background: var(--check-color);
  color: white;
}

.cal-filters__row:active .cal-filters__check {
  transform: scale(0.85);
}

.cal-filters__only {
  opacity: 0;
  color: var(--cal-text-muted);
  transition: opacity 0.15s ease;
}

@media (hover: none) {
  .cal-filters__only {
    opacity: 1;
  }
}
</style>
